import React, { useState, useRef } from "react";
import emailjs from "@emailjs/browser";
import { motion, AnimatePresence } from "framer-motion";
import { FaPaperPlane, FaUser, FaEnvelope, FaCommentDots, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";

const Contact = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendEmail = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      alert("Please fill all fields");
      return;
    }

    setStatus("sending");

    const templateParams = {
      name: formData.name,
      email: formData.email,
      message: formData.message,
    };

    emailjs.send(
      "shah",               
      "template_nbdvxso",   
      templateParams,
      "iNDJzwVCIGBv0T8uL"    
    )
    .then((response) => {
      setStatus("success");
      setFormData({ name: "", email: "", message: "" });
      setTimeout(() => setStatus("idle"), 5000);
    })
    .catch((err) => {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 5000);
    });
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-6 py-24 bg-[#fafafa] overflow-hidden">
      
      {/* --- NEW TECH BACKGROUND (Moving Circuit Grid) --- */}
      <div className="absolute inset-0 z-0">
        {/* Animated Lines Pattern */}
        <div className="absolute inset-0 opacity-[0.07]" 
          style={{ 
            backgroundImage: `linear-gradient(#808000 1.5px, transparent 1.5px), linear-gradient(90deg, #808000 1.5px, transparent 1.5px)`, 
            backgroundSize: '60px 60px' 
          }} 
        />
        {/* Moving Light Beam on Background */}
        <motion.div 
          animate={{ 
            top: ["-20%", "120%"],
            left: ["0%", "100%"]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
          className="absolute w-[500px] h-[2px] bg-gradient-to-r from-transparent via-[#808000]/40 to-transparent rotate-[45deg] blur-sm"
        />
        <motion.div 
          animate={{ 
            bottom: ["-20%", "120%"],
            right: ["0%", "100%"]
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "linear", delay: 2 }}
          className="absolute w-[500px] h-[2px] bg-gradient-to-r from-transparent via-[#800000]/30 to-transparent rotate-[-45deg] blur-sm"
        />
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-4xl grid grid-cols-1 lg:grid-cols-5 bg-white rounded-[3rem] shadow-[0_30px_100px_rgba(0,0,0,0.08)] border border-slate-100 overflow-hidden"
      >
        
        {/* --- LEFT SIDE: INFO --- */}
        <div className="lg:col-span-2 p-10 lg:p-14 text-white flex flex-col justify-between relative overflow-hidden"
             style={{ background: "linear-gradient(165deg, #808000 0%, #450a0a 100%)" }}>
          
          <div className="relative z-10">
            <h3 className="text-sm font-black uppercase tracking-[0.4em] opacity-70 mb-4">Contact Info</h3>
            <h2 className="text-4xl font-[1000] leading-tight uppercase tracking-tighter mb-8">
              Start a <br/> <span className="text-olive-300">Project.</span>
            </h2>
            <p className="text-olive-50/60 font-medium leading-relaxed">
              Have a question or want to work together? Send me a message and I'll get back to you within 24 hours.
            </p>
          </div>

          <div className="relative z-10 text-[10px] font-bold tracking-[0.2em] opacity-40">
            @codershahzad 2026
          </div>

          {/* Decorative Blur Background inside left panel */}
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        </div>

        {/* --- RIGHT SIDE: FORM --- */}
        <div className="lg:col-span-3 p-10 lg:p-14 bg-white">
          <form onSubmit={sendEmail} className="space-y-6">
            
            <div className="space-y-4">
              <div className="relative group">
                <FaUser className="absolute left-5 top-5 text-slate-300 group-focus-within:text-olive-600 transition-colors" />
                <input 
                  type="text" name="name" placeholder="Full Name"
                  value={formData.name} onChange={handleChange}
                  className="w-full pl-14 pr-6 py-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-olive-100 focus:bg-white outline-none font-bold text-slate-800 transition-all placeholder:font-medium"
                />
              </div>

              <div className="relative group">
                <FaEnvelope className="absolute left-5 top-5 text-slate-300 group-focus-within:text-olive-600 transition-colors" />
                <input 
                  type="email" name="email" placeholder="Email Address"
                  value={formData.email} onChange={handleChange}
                  className="w-full pl-14 pr-6 py-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-olive-100 focus:bg-white outline-none font-bold text-slate-800 transition-all placeholder:font-medium"
                />
              </div>

              <div className="relative group">
                <FaCommentDots className="absolute left-5 top-5 text-slate-300 group-focus-within:text-olive-600 transition-colors" />
                <textarea 
                  name="message" placeholder="Your Message" rows="4"
                  value={formData.message} onChange={handleChange}
                  className="w-full pl-14 pr-6 py-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-olive-100 focus:bg-white outline-none font-bold text-slate-800 transition-all resize-none placeholder:font-medium"
                />
              </div>
            </div>

            <motion.button 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={status === "sending"}
              className="w-full py-5 rounded-2xl text-white font-black uppercase tracking-[0.3em] text-xs shadow-xl flex items-center justify-center gap-4 relative overflow-hidden group disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #808000 0%, #800000 100%)" }}
            >
              <AnimatePresence mode="wait">
                {status === "sending" ? (
                  <motion.div key="sending" animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }}>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />
                  </motion.div>
                ) : status === "success" ? (
                  <motion.div key="success" initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-2">
                    <FaCheckCircle /> SENT SUCCESSFULLY
                  </motion.div>
                ) : status === "error" ? (
                  <motion.div key="error" className="flex items-center gap-2">
                    <FaExclamationTriangle /> SENDING FAILED
                  </motion.div>
                ) : (
                  <motion.div key="idle" className="flex items-center gap-4">
                    <span>Send Transmission</span>
                    <FaPaperPlane className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>

          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;